// Zebra Expr — runtime coercion helpers + the eager builtin-function registry.
//
// "Eager" builtins receive already-evaluated argument values. Short-circuit /
// lazy functions (IF, IFERR, DEFAULT, COALESCE, CASE, CHOOSE, ISERR) are
// handled in the interpreter because they must NOT evaluate every argument.

// A date-minus-date result. Structure types this as a DURATION and renders it
// readably ("3w 2d 4h"); we were rendering the raw millisecond delta
// ("1892160000"), which is what a bare `NOW() - Updated` column showed.
//
// It stays fully numeric for every downstream operation: valueOf() means the
// JS operators and every comparison still see milliseconds, and num() unwraps
// it explicitly, so `(NOW() - Created) / 86400000` and `dur > 5` behave exactly
// as before. Only its *string* form changed. The plain `__duration` marker (not
// instanceof) is what lets the CommonJS store writer recognise it across the
// ESM boundary — see server/formula-value.js.
export class Duration {
  constructor(ms) { this.ms = ms; this.__duration = true; }
  valueOf() { return this.ms; }
  toString() { return formatDuration(this.ms); }
  toJSON() { return formatDuration(this.ms); }
}

// Weeks / days / hours / minutes, zero units omitted — the same shape the
// Time in Status column already uses, so the two read alike in one grid.
export function formatDuration(ms) {
  if (!Number.isFinite(ms)) return '';
  const sign = ms < 0 ? '-' : '';
  let secs = Math.floor(Math.abs(ms) / 1000);
  const w = Math.floor(secs / 604800); secs -= w * 604800;
  const d = Math.floor(secs / 86400); secs -= d * 86400;
  const h = Math.floor(secs / 3600); secs -= h * 3600;
  const m = Math.floor(secs / 60);
  const parts = [];
  if (w) parts.push(`${w}w`);
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  return sign + (parts.length ? parts.join(' ') : '0m');
}

export function num(v) {
  if (typeof v === 'number') return v;
  if (v instanceof Duration) return v.ms;
  if (typeof v === 'boolean') return v ? 1 : 0;
  // Structure treats a blank/empty value as 0 in numeric context (so
  // `blank < 20` is true and a 0/0 ratio guarded by IF_ERROR(...,"") compares
  // as 0 — e.g. an empty folder's % Overdue rolls up to 0%, matching Jira).
  // Non-numeric text still yields NaN (parseFloat).
  if (v == null || v === '') return 0;
  return parseFloat(v);
}
// A date's TEXT form. Structure renders a date value as a date, and a formula
// result that is a date (DATE_ADD(duedate, 7), MAX{duedate}, TODAY()) used to
// reach the screen and the Formula Store as `String(date)` — "Thu Sep 17 2026
// 00:00:00 GMT+0000 (Coordinated Universal Time)" — and, through CONCAT, as
// the raw ISO timestamp. Date-only values (midnight) render as yyyy-MM-dd; a
// value with a time of day keeps it to the minute.
export function formatDateValue(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const ymd = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) return ymd;
  return `${ymd} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function str(v) {
  if (v == null) return '';
  if (v instanceof Date) return formatDateValue(v);
  // A list reads as its members, comma-separated, each formatted as a value
  // would be on its own (dates as dates, blanks dropped) — how Structure shows
  // an array in a cell, and what a `JOIN(list)` with the default separator
  // produces, so the two agree.
  if (Array.isArray(v)) return v.filter((x) => x != null && x !== '').map(str).join(', ');
  return String(v);
}
// Structure "truthiness": defined AND not 0 / "" / false / NaN.
export function isTruthy(v) {
  return !(v == null || v === false || v === 0 || v === '' ||
    (typeof v === 'number' && Number.isNaN(v)));
}

const round = (v, n = 0) => { const f = 10 ** (num(n) || 0); return Math.round(num(v) * f) / f; };

export function toDate(v) {
  if (v instanceof Date) return v;
  // A Duration must never coerce to a date: new Date(dur) would take valueOf()
  // as an epoch and silently turn an elapsed time into a 1970 timestamp.
  if (v instanceof Duration) return null;
  if (v == null || v === '') return null;
  // A DATE-ONLY value ("2026-09-10", how every date field arrives) is midnight
  // in the viewer's zone, the same instant TODAY(), MAKE_DATE and DATE_SET
  // produce. JavaScript parses that text as UTC midnight, so in any zone east
  // of Greenwich the field read as 02:00 local: `duedate = TODAY()` was never
  // true in a South African browser, and DATE_ADD(duedate, 7) rendered
  // "2026-09-17 02:00". Structure treats dates as user-zone midnight.
  if (typeof v === 'string') {
    const m = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(v);
    if (m) { const d = new Date(+m[1], +m[2] - 1, +m[3]); return Number.isNaN(d.getTime()) ? null : d; }
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
const DAY_MS = 86400000;

// Simple number formatter: "#,##0.00" style — we only honour thousands
// grouping (presence of a comma) and decimal count (digits after ".").
function formatNumber(v, pattern) {
  const x = num(v);
  if (Number.isNaN(x)) return '';
  const pat = str(pattern);
  const decimals = pat.includes('.') ? (pat.split('.')[1].match(/[0#]/g) || []).length : 0;
  const grouped = pat.includes(',');
  const prefix = (pat.match(/^[^#0.,]*/) || [''])[0];
  const suffix = (pat.match(/[^#0.,]*$/) || [''])[0];
  let s = Math.abs(x).toFixed(decimals);
  if (grouped) {
    const [intPart, dec] = s.split('.');
    s = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (dec ? '.' + dec : '');
  }
  return (x < 0 ? '-' : '') + prefix + s + suffix;
}

// Merge styling onto a value, preserving any styling already on it, so
// COLOR(BOLD(x), 'red') composes. Styled cells render as { text, color, … }.
function styled(value, patch) {
  const base = (value && typeof value === 'object' && 'text' in value) ? { ...value } : { text: str(value) };
  return { ...base, ...patch };
}

const pad2 = (x) => String(x).padStart(2, '0');
function formatDate(v, pattern) {
  const d = toDate(v);
  if (!d) return '';
  const map = {
    YYYY: d.getFullYear(), MM: pad2(d.getMonth() + 1), DD: pad2(d.getDate()),
    HH: pad2(d.getHours()), mm: pad2(d.getMinutes()), ss: pad2(d.getSeconds()),
  };
  return str(pattern).replace(/YYYY|MM|DD|HH|mm|ss/g, (t) => map[t]);
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// Jira Structure / ICU-style date-time formatter. Supports the common tokens:
// yyyy yy  MMMM MMM MM  dd d  EEEE EEE  HH  mm  ss  (case-sensitive). Longest
// token wins at each position; anything else (e.g. "-", " ", "/") is literal.
// Also: M (unpadded month), H/h/hh (24-/12-hour), a (AM/PM), m/s unpadded,
// and 'quoted text' passed through literally, as SimpleDateFormat does.
function formatDateTime(v, pattern) {
  const d = toDate(v);
  if (!d) return '';
  const h12 = d.getHours() % 12 || 12;
  const tok = {
    yyyy: d.getFullYear(), yy: pad2(d.getFullYear() % 100),
    MMMM: MONTH_NAMES[d.getMonth()], MMM: MONTH_NAMES[d.getMonth()].slice(0, 3), MM: pad2(d.getMonth() + 1), M: d.getMonth() + 1,
    dd: pad2(d.getDate()), d: d.getDate(),
    EEEE: DAY_NAMES[d.getDay()], EEE: DAY_NAMES[d.getDay()].slice(0, 3),
    HH: pad2(d.getHours()), H: d.getHours(), hh: pad2(h12), h: h12,
    mm: pad2(d.getMinutes()), m: d.getMinutes(), ss: pad2(d.getSeconds()), s: d.getSeconds(),
    a: d.getHours() < 12 ? 'AM' : 'PM',
  };
  return str(pattern == null ? 'yyyy-MM-dd' : pattern)
    .replace(/'([^']*)'|yyyy|yy|MMMM|MMM|MM|M|dd|d|EEEE|EEE|HH|H|hh|h|mm|m|ss|s|a/g, (t, quoted) => (quoted !== undefined ? quoted : String(tok[t])));
}

// A slash-delimited pattern (`/body/flags`) is a regex; anything else is a
// literal. `g` is forced on — Structure replaces every occurrence, and every
// pattern in the corpus depends on it. `$1`/`$2` backreferences come free from
// String.replace; `$20` degrades to `$2` + "0" exactly as Structure's does,
// which is what pads a single decimal to two.
const RE_LITERAL = /^\/(.*)\/([gimsuy]*)$/s;
function replaceLike(subject, pattern, replacement) {
  const m = RE_LITERAL.exec(pattern);
  if (m && m[1]) {
    try {
      const flags = m[2].includes('g') ? m[2] : `${m[2]}g`;
      return subject.replace(new RegExp(m[1], flags), replacement);
    } catch {
      // An unparseable regex falls through to a literal replace rather than
      // taking the whole cell down.
    }
  }
  return subject.split(pattern).join(replacement);
}

// Is this value a real date rather than a number that happens to parse? Mirrors
// interpreter.js's `dateish` — kept local so builtins stays dependency-free.
function looksLikeDate(v) {
  if (v instanceof Date) return true;
  return typeof v === 'string' && /\d{4}-\d\d|\d\d?\/\d\d?|T\d/.test(v) && toDate(v) != null;
}

// Reduce the contents of an array value (UMAX / UMIN / USUM / UAVG).
// Blanks are excluded rather than counted as 0 — the same posture MIN/MAX take,
// because the arrays these read come from `Array{if(cond, x)}` where every
// non-matching row contributes undefined. Returns null on an empty set so
// IS_EMPTY can catch it, instead of a misleading 0.
function uAgg(value, kind) {
  const arr = Array.isArray(value) ? value : (value == null ? [] : [value]);
  const nonEmpty = arr.filter((v) => v != null && v !== '');
  if (!nonEmpty.length) return null;
  // Date-aware for MIN/MAX: num() parseFloats "2026-08-07" to its YEAR, so a
  // numeric reduction over dates silently returns ~2026.
  if (kind === 'MAX' || kind === 'MIN') {
    const dates = nonEmpty.filter(looksLikeDate).map(toDate).filter(Boolean);
    if (dates.length === nonEmpty.length) {
      const ts = dates.map((d) => d.getTime());
      return new Date(kind === 'MAX' ? Math.max(...ts) : Math.min(...ts));
    }
  }
  const ns = nonEmpty.map(num).filter((x) => !Number.isNaN(x));
  if (!ns.length) return null;
  if (kind === 'SUM') return ns.reduce((p, c) => p + c, 0);
  if (kind === 'AVG') return ns.reduce((p, c) => p + c, 0) / ns.length;
  return kind === 'MAX' ? Math.max(...ns) : Math.min(...ns);
}

// Structure's date arithmetic takes a UNIT: DATE_ADD(date, n, "months"),
// DATE_SUBTRACT(date, n, "weeks"), DATE_SET(date, n, "day"). Singular, plural
// and case are all accepted; the unit defaults to days, which is what every
// two-argument DATE_ADD written against the old engine meant. Adding a month
// clamps to the target month's last day (Jan 31 + 1 month = Feb 28), the way
// Structure and every calendar library do — rolling into March would move a
// month-end deadline two days later.
const UNIT_ALIASES = {
  second: 'second', seconds: 'second', sec: 'second', s: 'second',
  minute: 'minute', minutes: 'minute', min: 'minute', m: 'minute',
  hour: 'hour', hours: 'hour', h: 'hour',
  day: 'day', days: 'day', d: 'day',
  week: 'week', weeks: 'week', w: 'week',
  month: 'month', months: 'month',
  year: 'year', years: 'year', y: 'year',
  day_of_week: 'day_of_week', dayofweek: 'day_of_week', weekday: 'day_of_week',
};
function unitOf(u, fallback = 'day') {
  const k = str(u).trim().toLowerCase();
  return UNIT_ALIASES[k] || fallback;
}
function addUnits(v, n, unit) {
  const d = toDate(v);
  if (!d) return null;
  const k = Math.trunc(num(n) || 0);
  const nd = new Date(d);
  switch (unitOf(unit)) {
    case 'second': nd.setSeconds(nd.getSeconds() + k); break;
    case 'minute': nd.setMinutes(nd.getMinutes() + k); break;
    case 'hour': nd.setHours(nd.getHours() + k); break;
    case 'week': nd.setDate(nd.getDate() + k * 7); break;
    case 'month': {
      const day = nd.getDate();
      nd.setDate(1);
      nd.setMonth(nd.getMonth() + k);
      nd.setDate(Math.min(day, new Date(nd.getFullYear(), nd.getMonth() + 1, 0).getDate()));
      break;
    }
    case 'year': {
      const day = nd.getDate();
      nd.setDate(1);
      nd.setFullYear(nd.getFullYear() + k);
      nd.setDate(Math.min(day, new Date(nd.getFullYear(), nd.getMonth() + 1, 0).getDate()));
      break;
    }
    default: nd.setDate(nd.getDate() + k);
  }
  return nd;
}
function setUnit(v, n, unit) {
  const d = toDate(v);
  if (!d) return null;
  const k = Math.trunc(num(n) || 0);
  const nd = new Date(d);
  switch (unitOf(unit, null)) {
    case 'second': nd.setSeconds(k); break;
    case 'minute': nd.setMinutes(k); break;
    case 'hour': nd.setHours(k); break;
    case 'day': nd.setDate(k); break;
    case 'month': nd.setMonth(k - 1); break; // Structure months are 1-based
    case 'year': nd.setFullYear(k); break;
    case 'day_of_week': nd.setDate(nd.getDate() + (k - isoWeekday(nd))); break; // 1 = Monday
    default: return null;
  }
  return nd;
}
// ISO-8601 weekday: Monday = 1 … Sunday = 7 (Structure's WEEKDAY). JS getDay()
// is Sunday = 0, which every "is it the weekend" formula copied from Structure
// read wrongly by one day.
const isoWeekday = (d) => ((d.getDay() + 6) % 7) + 1;
// ISO-8601 week number: the week containing the year's first Thursday is week 1.
function isoWeekNumber(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - day + 3);
  const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((t - firstThursday) / DAY_MS - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}
const truncTo = (v, unit) => {
  const d = toDate(v);
  if (!d) return null;
  const nd = new Date(d);
  if (unit === 'hour') nd.setMinutes(0, 0, 0);
  else if (unit === 'minute') nd.setSeconds(0, 0);
  else nd.setMilliseconds(0);
  return nd;
};
// Structure's PARSE_DATETIME(text, pattern): the same token alphabet
// FORMAT_DATETIME writes (yyyy yy MMMM MMM MM dd d HH mm ss), read back. Any
// other character in the pattern is a literal that must appear in the text.
function parseDateTime(text, pattern) {
  const s = str(text).trim();
  const pat = str(pattern);
  if (!s || !pat) return null;
  const order = [];
  const re = pat.replace(/yyyy|yy|MMMM|MMM|MM|dd|d|HH|mm|ss|[.*+?^${}()|[\]\\\/]/g, (t) => {
    switch (t) {
      case 'yyyy': order.push('y4'); return '(\\d{4})';
      case 'yy': order.push('y2'); return '(\\d{2})';
      case 'MMMM': order.push('Mname'); return '([A-Za-z]+)';
      case 'MMM': order.push('Mname'); return '([A-Za-z]{3})';
      case 'MM': order.push('M'); return '(\\d{1,2})';
      case 'dd': case 'd': order.push('d'); return '(\\d{1,2})';
      case 'HH': order.push('H'); return '(\\d{1,2})';
      case 'mm': order.push('mi'); return '(\\d{1,2})';
      case 'ss': order.push('s'); return '(\\d{1,2})';
      default: return '\\' + t; // a regex metachar used literally in the pattern
    }
  });
  const m = new RegExp('^' + re + '$').exec(s);
  if (!m) return null;
  const parts = { y: 1970, M: 1, d: 1, H: 0, mi: 0, s: 0 };
  order.forEach((k, i) => {
    const v = m[i + 1];
    if (k === 'y4') parts.y = +v;
    else if (k === 'y2') parts.y = 2000 + +v;
    else if (k === 'Mname') { const idx = MONTH_NAMES.findIndex((n) => n.toLowerCase().startsWith(v.toLowerCase().slice(0, 3))); if (idx >= 0) parts.M = idx + 1; }
    else parts[k] = +v;
  });
  const d = new Date(parts.y, parts.M - 1, parts.d, parts.H, parts.mi, parts.s);
  return Number.isNaN(d.getTime()) ? null : d;
}
// Structure's text patterns: "/…/" is a regular expression, `*` / `?` are
// wildcards, anything else is a literal. Shared by MATCH and SEARCH so the two
// read a pattern identically; REPLACE has its own (replaceLike) because it also
// needs the flags.
export function patternRegex(pattern) {
  const pat = str(pattern);
  const m = RE_LITERAL.exec(pat);
  if (m && m[1]) { try { return new RegExp(m[1], m[2].replace('g', '')); } catch { /* fall through */ } }
  if (pat.includes('*') || pat.includes('?')) {
    return new RegExp('^' + pat.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.') + '$', 'i');
  }
  return null;
}

// Universal comparison — Structure's ordering for SORT / SORT_BY / UMAX /
// UMIN: two dates by instant, two numbers (or numeric text) numerically,
// anything else as text. Empty sorts first.
export function ucmp(a, b) {
  const ea = a == null || a === '', eb = b == null || b === '';
  if (ea && eb) return 0;
  if (ea) return -1;
  if (eb) return 1;
  if (looksLikeDate(a) && looksLikeDate(b)) return toDate(a).getTime() - toDate(b).getTime();
  const na = typeof a === 'boolean' ? NaN : num(a), nb = typeof b === 'boolean' ? NaN : num(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && typeof a !== 'boolean' && typeof b !== 'boolean') return na - nb;
  return str(a).localeCompare(str(b));
}

// Nth percentile of a numeric list with linear interpolation between ranks
// (the inclusive definition spreadsheets use, so MEDIAN of [1,2,3,4] is 2.5).
// p may be a fraction (0.95) or a percentage (95).
export function percentile(list, p) {
  const s = [...list].sort((a, b) => a - b);
  if (!s.length) return null;
  let f = num(p);
  if (Number.isNaN(f)) return null;
  if (f > 1) f /= 100;
  f = Math.min(1, Math.max(0, f));
  const pos = (s.length - 1) * f;
  const lo = Math.floor(pos), hi = Math.ceil(pos);
  return lo === hi ? s[lo] : s[lo] + (s[hi] - s[lo]) * (pos - lo);
}
const stdev = (list, sample) => {
  const ns = list.map(num).filter((x) => !Number.isNaN(x));
  const n = ns.length;
  if (n < (sample ? 2 : 1)) return null;
  const mean = ns.reduce((p, c) => p + c, 0) / n;
  const ss = ns.reduce((p, c) => p + (c - mean) ** 2, 0);
  return Math.sqrt(ss / (sample ? n - 1 : n));
};
// Numeric arguments for the variadic reducers: SUM(a, b, c) and SUM(list) are
// both Structure — one array argument is spread, blanks are ignored.
const numArgs = (a) => a.flat(1).filter((v) => v != null && v !== '').map(num).filter((x) => !Number.isNaN(x));

// Durations. DURATION("1d 3h 30m") reads Jira's duration text on the calendar
// clock (1d = 24h, 1w = 7d); JIRA_DURATION reads it on the work clock (1d = 8h,
// 1w = 5d), which is how Jira's own estimate fields are written. Both return a
// Duration, so the value renders readably and stays milliseconds in arithmetic.
const HOUR_MS = 3600000;
function parseDuration(text, hoursPerDay, daysPerWeek) {
  const s = str(text).trim().toLowerCase();
  if (!s) return null;
  if (/^-?\d+(\.\d+)?$/.test(s)) return new Duration(parseFloat(s)); // already milliseconds
  const unit = { w: daysPerWeek * hoursPerDay * HOUR_MS, d: hoursPerDay * HOUR_MS, h: HOUR_MS, m: 60000, s: 1000 };
  let ms = 0, matched = false;
  const re = /(-?\d+(?:\.\d+)?)\s*(w|d|h|m|s)\b/g;
  let m;
  while ((m = re.exec(s))) { ms += parseFloat(m[1]) * unit[m[2]]; matched = true; }
  return matched ? new Duration(ms) : null;
}
const durMs = (v) => (v instanceof Duration ? v.ms : (v == null || v === '' ? NaN : num(v)));

// Structure's text SPLIT takes a pattern: "/…/" regex, `*`/`?` wildcards, or a
// literal separator (a wildcard pattern splits on whatever matches it, not on
// the whole string, so it is compiled without the anchors MATCH uses).
function splitPattern(sep) {
  const pat = str(sep);
  const m = RE_LITERAL.exec(pat);
  if (m && m[1]) { try { return new RegExp(m[1], m[2].replace('g', '')); } catch { /* literal */ } }
  if (pat.includes('*') || pat.includes('?')) {
    return new RegExp(pat.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*?').replace(/\?/g, '.'));
  }
  return pat;
}

export const BUILTINS = {
  // ---- numeric ----
  ROUND: (a) => round(a[0], a[1]),
  FLOOR: (a) => { const f = 10 ** (num(a[1]) || 0); return Math.floor(num(a[0]) * f) / f; },
  CEILING: (a) => { const f = 10 ** (num(a[1]) || 0); return Math.ceil(num(a[0]) * f) / f; },
  ABS: (a) => Math.abs(num(a[0])),
  MOD: (a) => num(a[0]) % num(a[1]),
  POW: (a) => Math.pow(num(a[0]), num(a[1])),
  SIGN: (a) => Math.sign(num(a[0])),
  SQRT: (a) => Math.sqrt(num(a[0])),
  NUMBER: (a) => { const x = num(a[0]); return Number.isNaN(x) ? (a.length > 1 ? num(a[1]) : NaN) : x; },
  // Variadic over arguments OR over one list argument (Structure: SUM(a, b)
  // and SUM(list) are both valid). Empty → undefined, so IS_EMPTY catches it.
  SUM: (a) => numArgs(a).reduce((p, c) => p + c, 0),
  MIN: (a) => { const n = numArgs(a); return n.length ? Math.min(...n) : undefined; },
  MAX: (a) => { const n = numArgs(a); return n.length ? Math.max(...n) : undefined; },
  AVERAGE: (a) => { const n = numArgs(a); return n.length ? n.reduce((p, c) => p + c, 0) / n.length : undefined; },
  MUL: (a) => { const n = numArgs(a); return n.length ? n.reduce((p, c) => p * c, 1) : undefined; },
  SQR: (a) => { const x = num(a[0]); return x * x; },
  LOG10: (a) => Math.log10(num(a[0])),
  MEDIAN: (a) => percentile(numArgs(a), 0.5),
  // PERCENTILE(list, p): p as a fraction (0.9) or percentage (90).
  PERCENTILE: (a) => percentile(numArgs([a[0]]), a[1]),
  // QUARTILE(list, n): 0 = min, 1 = 25th, 2 = median, 3 = 75th, 4 = max.
  QUARTILE: (a) => { const q = Math.round(num(a[1])); if (q < 0 || q > 4 || Number.isNaN(q)) return null; return percentile(numArgs([a[0]]), q / 4); },
  STDEV: (a) => stdev(numArgs(a), true),
  STDEVP: (a) => stdev(numArgs(a), false),
  FORMAT: (a) => formatNumber(a[0], a[1]),

  // Structure's U-prefixed aggregates reduce the CONTENTS of a single array
  // value — typically `Array{…}` collected over a subtree — where MAX(a,b,c) is
  // variadic over its argument list. Aliasing UMAX to MAX would therefore hand
  // Math.max one array and return NaN, which is why "Order with Longest Lead
  // Time" (insight 62) rendered blank: the unknown-function throw was swallowed
  // by its surrounding IF_ERROR, so 6,609 rows lost their value with no error
  // surfaced anywhere.
  //
  // "Universal" MAX/MIN: any comparable type — numbers, dates, text — over
  // the arguments or over one list argument (ucmp). Blanks are skipped.
  UMAX: (a) => { const l = a.flat(1).filter((v) => v != null && v !== ''); return l.length ? l.reduce((p, c) => (ucmp(c, p) > 0 ? c : p)) : null; },
  UMIN: (a) => { const l = a.flat(1).filter((v) => v != null && v !== ''); return l.length ? l.reduce((p, c) => (ucmp(c, p) < 0 ? c : p)) : null; },
  USUM: (a) => uAgg(a[0], 'SUM'),
  UAVG: (a) => uAgg(a[0], 'AVG'),

  // ---- text ----
  CONCAT: (a) => a.map(str).join(''),
  UPPER: (a) => str(a[0]).toUpperCase(),
  LOWER: (a) => str(a[0]).toLowerCase(),
  TRIM: (a) => str(a[0]).trim(),
  LEN: (a) => str(a[0]).length,
  LEFT: (a) => str(a[0]).slice(0, Math.max(0, num(a[1]) || 0)),
  RIGHT: (a) => { const s = str(a[0]); const n2 = Math.max(0, num(a[1]) || 0); return n2 ? s.slice(-n2) : ''; },
  SUBSTRING: (a) => str(a[0]).slice(num(a[1]) || 0, a[2] != null ? num(a[2]) : undefined),
  // Structure's replace(). The pattern is a REGEX when written slash-delimited
  // (`"/(\\d{3})/"`) and a literal otherwise (`","`) — that distinction is what
  // the migrated the archive client formulas rely on, and the corpus uses exactly four
  // patterns: one literal comma and three slash-delimited regexes.
  //
  // Both forms replace EVERY occurrence: the money formatter chains
  //   .replace("/(\\d{1,3})(?=(\\d{3})+([^\\d]|$))/", "$1,").replace(",", " ")
  // to turn 47831873.45 into "47 831 873.45", which needs every thousands
  // group and then every comma. Treating the slash form as a literal — what
  // this did before — matched nothing, so the number came out unseparated.
  REPLACE: (a) => replaceLike(str(a[0]), str(a[1]), str(a[2])),
  TEXT: (a) => str(a[0]),
  CONTAINS: (a) => (Array.isArray(a[0]) ? a[0].some((x) => x === a[1] || str(x) === str(a[1])) : str(a[0]).includes(str(a[1]))),
  SPLIT: (a) => str(a[0]).split(splitPattern(a[1])),
  // MATCH(value, pattern): "/…/" regex, `*`/`?` wildcard, else a
  // case-insensitive substring — the three pattern shapes Structure accepts.
  MATCH: (a) => {
    const v = str(a[0]);
    const re = patternRegex(a[1]);
    if (re) return re.test(v);
    return v.toLowerCase().includes(str(a[1]).toLowerCase());
  },
  // SEARCH(pattern, value): 1-based position of the first match, or empty when
  // there is none (Structure's argument order — pattern FIRST).
  // An optional third argument is the 1-based position to start from.
  SEARCH: (a) => {
    const from = a[2] == null ? 0 : Math.max(0, (Math.trunc(num(a[2])) || 1) - 1);
    const v = str(a[1]).slice(from);
    const re = patternRegex(a[0]);
    const idx = re ? v.search(re) : v.toLowerCase().indexOf(str(a[0]).toLowerCase());
    return idx < 0 ? undefined : idx + 1 + from;
  },
  // EXACT(a, b): case-SENSITIVE text equality (the `=` operator is not).
  // Structure treats undefined as equal to undefined and to the empty string.
  EXACT: (a) => str(a[0]) === str(a[1]),
  // MID(text, index, count): `count` characters from a 1-BASED index
  // (spreadsheet convention; SUBSTRING is the 0-based, end-exclusive form).
  MID: (a) => { const s = str(a[0]); const i = Math.max(1, Math.trunc(num(a[1])) || 1); const n2 = a[2] == null ? undefined : Math.max(0, Math.trunc(num(a[2])) || 0); return s.substr(i - 1, n2); },
  // REPLACE_AT(text, index, count, replacement): splice at a 1-based index.
  REPLACE_AT: (a) => { const s = str(a[0]); const i = Math.max(1, Math.trunc(num(a[1])) || 1); const n2 = Math.max(0, Math.trunc(num(a[2])) || 0); return s.slice(0, i - 1) + str(a[3]) + s.slice(i - 1 + n2); },
  URL_ENCODE: (a) => encodeURIComponent(str(a[0])).replace(/%20/g, '+'),
  URL_DECODE: (a) => { try { return decodeURIComponent(str(a[0]).replace(/\+/g, ' ')); } catch { return str(a[0]); } },

  // ---- array ----
  SIZE: (a) => (Array.isArray(a[0]) ? a[0].length : (a[0] == null ? 0 : 1)),
  FIRST: (a) => (Array.isArray(a[0]) ? a[0][0] : a[0]),
  LAST: (a) => (Array.isArray(a[0]) ? a[0][a[0].length - 1] : a[0]),
  IS_EMPTY: (a) => (Array.isArray(a[0]) ? a[0].length === 0 : (a[0] == null || a[0] === '')),
  // Natural ordering (ucmp): numbers numerically, dates by instant, text
  // alphabetically — a plain text sort put 10 before 9.
  SORT: (a) => (Array.isArray(a[0]) ? [...a[0]].sort(ucmp) : a[0]),
  UNIQUE: (a) => (Array.isArray(a[0]) ? [...new Set(a[0])] : a[0]),
  // ARRAY(a, b, c) — the constructor; ARRAY{…} is the aggregate.
  ARRAY: (a) => [...a],
  COMPACT: (a) => (Array.isArray(a[0]) ? a[0].filter((x) => x !== undefined && x !== null) : a[0]),
  CONTAINS_ALL: (a) => { const l = Array.isArray(a[0]) ? a[0] : []; const w = Array.isArray(a[1]) ? a[1] : [a[1]]; return w.every((x) => l.some((y) => y === x || str(y) === str(x))); },
  CONTAINS_ANY: (a) => { const l = Array.isArray(a[0]) ? a[0] : []; const w = Array.isArray(a[1]) ? a[1] : [a[1]]; return w.some((x) => l.some((y) => y === x || str(y) === str(x))); },
  // GET(list, index) — 0-based element; GET(map, "name") — a property.
  GET: (a) => {
    if (Array.isArray(a[0])) { const i = Math.trunc(num(a[1])); return i >= 0 ? a[0][i] : undefined; }
    if (a[0] && typeof a[0] === 'object') return a[0][str(a[1])];
    return undefined;
  },
  INDEXES: (a) => (Array.isArray(a[0]) ? a[0].map((_, i) => i) : []),
  IS_ARRAY: (a) => Array.isArray(a[0]),
  LAST_INDEX_OF: (a) => { if (!Array.isArray(a[0])) return undefined; for (let i = a[0].length - 1; i >= 0; i--) { if (a[0][i] === a[1] || str(a[0][i]) === str(a[1])) return i; } return undefined; },
  MERGE_ARRAYS: (a) => a.flatMap((x) => (Array.isArray(x) ? x : (x == null ? [] : [x]))),
  RECURSIVE_FLATTEN: (a) => (Array.isArray(a[0]) ? a[0].flat(Infinity).filter((x) => x !== undefined) : a[0]),
  SEQUENCE: (a) => { const from = Math.trunc(num(a[0])), to = Math.trunc(num(a[1])); if (Number.isNaN(from) || Number.isNaN(to)) return []; const out = []; if (from <= to) { for (let i = from; i <= to; i++) out.push(i); } else { for (let i = from; i >= to; i--) out.push(i); } return out; },
  // SUBARRAY(list, from, to): 0-based, `to` exclusive (SLICE is the alias).
  SUBARRAY: (a) => (Array.isArray(a[0]) ? a[0].slice(num(a[1]) || 0, a[2] != null ? num(a[2]) : undefined) : []),
  WITHOUT: (a) => (Array.isArray(a[0]) ? a[0].filter((x) => !(x === a[1] || str(x) === str(a[1]))) : a[0]),

  // ---- logic / null ----
  DEFINED: (a) => a[0] != null,
  IS_NULL: (a) => a[0] == null,

  // ---- date / duration ----
  TODAY: () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; },
  NOW: () => new Date(),
  YEAR: (a) => { const d = toDate(a[0]); return d ? d.getFullYear() : NaN; },
  MONTH: (a) => { const d = toDate(a[0]); return d ? d.getMonth() + 1 : NaN; },
  DAY: (a) => { const d = toDate(a[0]); return d ? d.getDate() : NaN; },
  DAYS_BETWEEN: (a) => { const x = toDate(a[0]); const y = toDate(a[1]); return x && y ? Math.round((y - x) / DAY_MS) : NaN; },
  // Full hours between two date/times (Structure): truncated, signed.
  HOURS_BETWEEN: (a) => { const x = toDate(a[0]); const y = toDate(a[1]); return x && y ? Math.trunc((y - x) / 3600000) : NaN; },
  DATE_ADD: (a) => addUnits(a[0], a[1], a[2]),
  DATE_SUBTRACT: (a) => addUnits(a[0], -(num(a[1]) || 0), a[2]),
  // DATE_SET(date, number, unit) per Structure; (date, unit, number) is read
  // just as naturally, so both orders are accepted.
  DATE_SET: (a) => (typeof a[1] === 'string' && typeof a[2] !== 'string' ? setUnit(a[0], a[2], a[1]) : setUnit(a[0], a[1], a[2])),
  MAKE_DATE: (a) => { const y = num(a[0]); if (Number.isNaN(y)) return null; return new Date(y, (num(a[1]) || 1) - 1, num(a[2]) || 1); },
  MAKE_DATETIME: (a) => { const y = num(a[0]); if (Number.isNaN(y)) return null; return new Date(y, (num(a[1]) || 1) - 1, num(a[2]) || 1, num(a[3]) || 0, num(a[4]) || 0, num(a[5]) || 0); },
  // Structure's DATETIME(text) — text to a timestamp. A Date passes through.
  DATETIME: (a) => toDate(a[0]),
  PARSE_DATETIME: (a) => parseDateTime(a[0], a[1]),
  TRUNCATE_TO_HOURS: (a) => truncTo(a[0], 'hour'),
  TRUNCATE_TO_MINUTES: (a) => truncTo(a[0], 'minute'),
  TRUNCATE_TO_SECONDS: (a) => truncTo(a[0], 'second'),
  IS_OVERDUE: (a) => { const d = toDate(a[0]); const t = new Date(); t.setHours(0, 0, 0, 0); return d ? d < t : false; },
  // Structure parity: drop the time-of-day so date subtraction yields whole
  // days (`TRUNCATE_TIME(statusCategoryChanged) - poDate`) instead of a
  // fractional day-plus-hours delta.
  TRUNCATE_TIME: (a) => { const d = toDate(a[0]); if (!d) return null; const nd = new Date(d); nd.setHours(0, 0, 0, 0); return nd; },

  // ---- duration ----
  DURATION: (a) => parseDuration(a[0], 24, 7),
  JIRA_DURATION: (a) => parseDuration(a[0], 8, 5),
  FORMAT_DURATION: (a) => { const ms = durMs(a[0]); return Number.isFinite(ms) ? formatDuration(ms) : ''; },
  CALENDAR_SECONDS: (a) => { const ms = durMs(a[0]); return Number.isFinite(ms) ? ms / 1000 : undefined; },
  CALENDAR_MINUTES: (a) => { const ms = durMs(a[0]); return Number.isFinite(ms) ? ms / 60000 : undefined; },
  CALENDAR_HOURS: (a) => { const ms = durMs(a[0]); return Number.isFinite(ms) ? ms / HOUR_MS : undefined; },
  CALENDAR_DAYS: (a) => { const ms = durMs(a[0]); return Number.isFinite(ms) ? ms / (24 * HOUR_MS) : undefined; },
  // Work-clock conversions: Jira's default 8-hour day and 5-day week.
  JIRA_DAYS: (a) => { const ms = durMs(a[0]); return Number.isFinite(ms) ? ms / (8 * HOUR_MS) : undefined; },
  JIRA_WEEKS: (a) => { const ms = durMs(a[0]); return Number.isFinite(ms) ? ms / (40 * HOUR_MS) : undefined; },
  // CALENDAR_DURATION(start, finish): elapsed time on the 24/7 calendar. A
  // named work calendar is a Structure/Tempo server object we do not have —
  // asking for one is an error rather than a silently wrong number.
  CALENDAR_DURATION: (a) => {
    if (a[2] != null && a[2] !== '') throw new Error(`Named calendar "${str(a[2])}" is not available; CALENDAR_DURATION(start, finish) uses the 24/7 calendar`);
    const x = toDate(a[0]); const y = toDate(a[1]);
    return x && y ? new Duration(y.getTime() - x.getTime()) : undefined;
  },

  // ---- more numeric ----
  TRUNC: (a) => Math.trunc(num(a[0])),
  INT: (a) => Math.floor(num(a[0])),
  ROUNDUP: (a) => { const f = 10 ** (num(a[1]) || 0); return Math.ceil(num(a[0]) * f) / f; },
  ROUNDDOWN: (a) => { const f = 10 ** (num(a[1]) || 0); return Math.floor(num(a[0]) * f) / f; },
  CLAMP: (a) => Math.min(Math.max(num(a[0]), num(a[1])), num(a[2])),
  EXP: (a) => Math.exp(num(a[0])),
  LN: (a) => Math.log(num(a[0])),
  LOG: (a) => (a[1] != null ? Math.log(num(a[0])) / Math.log(num(a[1])) : Math.log10(num(a[0]))),
  PI: () => Math.PI,
  E: () => Math.E,

  // ---- more text ----
  STARTS_WITH: (a) => str(a[0]).toLowerCase().startsWith(str(a[1]).toLowerCase()),
  ENDS_WITH: (a) => str(a[0]).toLowerCase().endsWith(str(a[1]).toLowerCase()),
  // INDEX_OF(array, element) is Structure's: 0-based, empty when absent. The
  // text form (INDEX_OF(text, part)) is kept for formulas already written to it.
  INDEX_OF: (a) => {
    if (Array.isArray(a[0])) { const i = a[0].findIndex((x) => x === a[1] || str(x) === str(a[1])); return i < 0 ? undefined : i; }
    return str(a[0]).indexOf(str(a[1]));
  },
  REPEAT: (a) => str(a[0]).repeat(Math.max(0, Math.floor(num(a[1])) || 0)),
  PROPER: (a) => str(a[0]).replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()),
  PAD_LEFT: (a) => str(a[0]).padStart(Math.max(0, num(a[1]) || 0), a[2] != null ? str(a[2]) : ' '),
  PAD_RIGHT: (a) => str(a[0]).padEnd(Math.max(0, num(a[1]) || 0), a[2] != null ? str(a[2]) : ' '),
  REGEX_TEST: (a) => { try { return new RegExp(str(a[1]), a[2] != null ? str(a[2]) : '').test(str(a[0])); } catch { return false; } },
  REGEX_REPLACE: (a) => { try { return str(a[0]).replace(new RegExp(str(a[1]), a[3] != null ? str(a[3]) : 'g'), str(a[2])); } catch { return str(a[0]); } },
  REGEX_EXTRACT: (a) => { try { const m = str(a[0]).match(new RegExp(str(a[1]), a[2] != null ? str(a[2]) : '')); return m ? (m[1] != null ? m[1] : m[0]) : ''; } catch { return ''; } },

  // ---- more array ----
  COUNT: (a) => (Array.isArray(a[0]) ? a[0].filter((x) => x != null && x !== '').length : (a[0] == null || a[0] === '' ? 0 : 1)),
  JOIN: (a) => { const arr = Array.isArray(a[0]) ? a[0] : [a[0]]; const sep = a[1] != null ? str(a[1]) : ', '; return arr.filter((x) => x != null && x !== '').map(str).join(sep); },
  REVERSE: (a) => (Array.isArray(a[0]) ? [...a[0]].reverse() : str(a[0]).split('').reverse().join('')),
  SLICE: (a) => { const src = Array.isArray(a[0]) ? a[0] : str(a[0]); return src.slice(num(a[1]) || 0, a[2] != null ? num(a[2]) : undefined); },
  INDEX: (a) => (Array.isArray(a[0]) ? a[0][num(a[1]) || 0] : undefined),
  // One level, as Structure's FLATTEN; RECURSIVE_FLATTEN goes all the way.
  FLATTEN: (a) => (Array.isArray(a[0]) ? a[0].flat(1) : a[0]),

  // ---- logic (function forms; `x AND y` operators also exist) ----
  NOT: (a) => !isTruthy(a[0]),
  AND: (a) => a.every(isTruthy),
  OR: (a) => a.some(isTruthy),
  XOR: (a) => a.filter(isTruthy).length % 2 === 1,
  BOOL: (a) => isTruthy(a[0]),
  IS_NUMBER: (a) => typeof a[0] === 'number' && !Number.isNaN(a[0]),
  IS_TEXT: (a) => typeof a[0] === 'string',
  IS_LIST: (a) => Array.isArray(a[0]),

  // ---- more date ----
  // Structure's DATE(text) converts text to a date at midnight. The old
  // engine only knew DATE(year, month, day) — Structure's MAKE_DATE — so a
  // pasted `DATE("2026-03-05")` parsed "2026-03-05" as the year 2026 and
  // returned the 1st of January 2026. Both shapes are honoured: one text
  // argument converts, three numbers build.
  DATE: (a) => {
    if (a.length >= 3 && typeof a[0] === 'number') return new Date(a[0], (num(a[1]) || 1) - 1, num(a[2]) || 1);
    const d = toDate(a[0]); if (!d) return null; const nd = new Date(d); nd.setHours(0, 0, 0, 0); return nd;
  },
  HOUR: (a) => { const d = toDate(a[0]); return d ? d.getHours() : NaN; },
  MINUTE: (a) => { const d = toDate(a[0]); return d ? d.getMinutes() : NaN; },
  SECOND: (a) => { const d = toDate(a[0]); return d ? d.getSeconds() : NaN; },
  WEEKDAY: (a) => { const d = toDate(a[0]); return d ? isoWeekday(d) : NaN; },
  WEEKNUM: (a) => { const d = toDate(a[0]); return d ? isoWeekNumber(d) : NaN; },
  MONTHS_BETWEEN: (a) => { const x = toDate(a[0]), y = toDate(a[1]); return x && y ? (y.getFullYear() - x.getFullYear()) * 12 + (y.getMonth() - x.getMonth()) : NaN; },
  YEARS_BETWEEN: (a) => { const x = toDate(a[0]), y = toDate(a[1]); return x && y ? y.getFullYear() - x.getFullYear() : NaN; },
  ADD_MONTHS: (a) => { const d = toDate(a[0]); if (!d) return null; const nd = new Date(d); nd.setMonth(nd.getMonth() + (num(a[1]) || 0)); return nd; },
  START_OF_MONTH: (a) => { const d = toDate(a[0]); if (!d) return null; return new Date(d.getFullYear(), d.getMonth(), 1); },
  END_OF_MONTH: (a) => { const d = toDate(a[0]); if (!d) return null; return new Date(d.getFullYear(), d.getMonth() + 1, 0); },
  FORMAT_DATE: (a) => formatDate(a[0], a[1] != null ? a[1] : 'YYYY-MM-DD'),
  FORMAT_DATETIME: (a) => formatDateTime(a[0], a[1]),

  // ---- styled / panel cells (render as coloured cells) ----
  COLOR: (a) => styled(a[0], { color: str(a[1]) }),
  BACKGROUND: (a) => styled(a[0], { backgroundColor: str(a[1]) }),
  BOLD: (a) => styled(a[0], { fontWeight: 'bold' }),
  PANEL: (a) => styled(a[0], { backgroundColor: str(a[1]), ...(a[2] != null ? { color: str(a[2]) } : {}) }),
};

// Names the interpreter handles specially (must not eager-evaluate all args).
export const LAZY = new Set(['IF', 'IFERR', 'ISERR', 'DEFAULT', 'COALESCE', 'CASE', 'CHOOSE']);
